import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';

interface PlanLimit {
  allowed: boolean;
  remaining: number | null;  // null = unlimited
  limit: number | null;
  used: number;
}

interface TeamPlan {
  plan_id: string;
  plan_name: string;
  limits: {
    ai_reviews_per_month: number | null;
    team_members: number | null;
    _order?: string[];
    _labels?: Record<string, string>;
    [key: string]: number | null | string[] | Record<string, string> | undefined;
  };
  features: Record<string, boolean | string[] | Record<string, string>>;
}

// Cache for permission checks to reduce DB calls
const CACHE_TTL = 30000; // 30 seconds

export function useEntitlements() {
  const { user } = useAuth();
  const { userAccount, activeTeam, isStaff, isSuperadmin } = useUser();
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [teamPlan, setTeamPlan] = useState<TeamPlan | null>(null);
  // null = RPC check not yet resolved for the current user
  const [checkedPublicNote, setCheckedPublicNote] = useState<boolean | null>(null);

  // Use ref for cache to avoid re-renders
  const cacheRef = useRef<Map<string, { result: boolean; timestamp: number }>>(new Map());

  // Get active team ID (falls back to primary_team_id for backwards compatibility)
  const activeTeamId = activeTeam?.team_id || userAccount?.primary_team_id || null;

  // Clear cache when user or active team changes
  const invalidateCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  // Check a single permission via RPC
  // Superadmin bypasses all checks; staff and regular users go through DB function
  const hasPermission = useCallback(async (
    permissionId: string,
    teamId?: string
  ): Promise<boolean> => {
    if (!user) return false;

    // Superadmin bypass - always has permission
    if (isSuperadmin) return true;

    const effectiveTeamId = teamId || activeTeamId;

    // Staff users don't need a team context (DB function handles staff role/plan check)
    // Regular users need a team context
    if (!isStaff && !effectiveTeamId) return false;

    // Check cache first
    const cacheKey = `${user.id}:${permissionId}:${effectiveTeamId || 'staff'}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.result;
    }

    try {
      // has_permission DB function handles staff role/plan evaluation and regular AND model
      const { data, error } = await supabase.rpc('has_permission', {
        check_user_id: user.id,
        check_permission_id: permissionId,
        check_team_id: effectiveTeamId
      });

      if (error) {
        console.error('Permission check error:', error);
        return false;
      }

      const result = data === true;

      // Cache the result
      cacheRef.current.set(cacheKey, { result, timestamp: Date.now() });

      return result;
    } catch (err) {
      console.error('Permission check failed:', err);
      return false;
    }
  }, [user, isSuperadmin, isStaff, activeTeamId]);

  // Check plan limits for a permission (usage tracking removed - always returns unlimited)
  const checkPlanLimit = useCallback(async (
    permissionId: string,
    teamId?: string
  ): Promise<PlanLimit> => {
    const checkTeamId = teamId || activeTeamId;

    if (!checkTeamId) {
      return { allowed: false, remaining: 0, limit: 0, used: 0 };
    }

    try {
      // Query plan_entitlement through team's plan_id
      const { data, error } = await supabase
        .from('team')
        .select(`
          plan_id,
          plan_entitlement:plan_id (
            permission_id,
            usage_limit
          )
        `)
        .eq('team_id', checkTeamId)
        .single();

      if (error) {
        console.error('Plan limit check error:', error);
        return { allowed: false, remaining: 0, limit: 0, used: 0 };
      }

      // Find the specific permission entitlement
      const entitlements = (data?.plan_entitlement || []) as Array<{ permission_id: string; usage_limit: number | null }>;
      const entitlement = entitlements.find(e => e.permission_id === permissionId);

      if (!entitlement) {
        // No plan entitlement, but user might have permission from role
        const hasRolePerm = await hasPermission(permissionId, checkTeamId);
        return {
          allowed: hasRolePerm,
          remaining: hasRolePerm ? null : 0,
          limit: null,
          used: 0
        };
      }

      // Usage tracking removed - always report 0 used
      return {
        allowed: true,
        remaining: entitlement.usage_limit,
        limit: entitlement.usage_limit,
        used: 0
      };
    } catch (err) {
      console.error('Plan limit check failed:', err);
      return { allowed: false, remaining: 0, limit: 0, used: 0 };
    }
  }, [activeTeamId, hasPermission]);

  // Fetch team's plan info
  const fetchTeamPlan = useCallback(async (teamId: string) => {
    try {
      const { data, error } = await supabase
        .from('team')
        .select(`
          plan_id,
          team_plan:plan_id (
            plan_id,
            plan_name,
            limits,
            features
          )
        `)
        .eq('team_id', teamId)
        .single();

      if (error) {
        console.error('Failed to fetch team plan:', error);
        return null;
      }

      const plan = data?.team_plan as any;
      if (plan) {
        setTeamPlan({
          plan_id: plan.plan_id,
          plan_name: plan.plan_name,
          limits: plan.limits || {},
          features: plan.features || {}
        });
      }
      return plan;
    } catch (err) {
      console.error('Team plan fetch failed:', err);
      return null;
    }
  }, []);

  // Common permissions to pre-load
  const commonPermissions = useMemo(() => [
    'update_content_visibility',
    'create_ai_review',
    'update_ai_prompts',
    'update_role_permissions',
    'create_characteristic',
    'read_characteristic',
    'update_characteristic',
    'delete_characteristic',
    'read_issuer_source',
    'update_issuer_source',
    'read_issuer_config',
    'update_issuer_config',
    'view_component_ai_review_settings',
    'view_component_ai_review_history',
    'view_component_source_management',
    'view_component_research_insights',
    'view_component_issuer_research',
    'view_component_activity_feed',
    'view_component_human_review_status',
    'update_issuer_human_review',
    // Admin page permissions
    'view_page_ai_costs',
    'view_page_prompt_config',
    'view_page_admin_users',
    'view_page_admin_teams',
    'view_page_admin_role_entitlements',
    'view_page_admin_plan_entitlements',
    'view_page_admin_agent_coverage',
    'view_page_admin_audit',
    'view_page_unmatched_holdings',
    'view_page_asset_cat_mapping',
    'update_asset_cat_mapping',
    // TOS Management permissions
    'view_page_tos_management',
    'update_tos',
    // Report Disclaimer permissions
    'view_page_report_disclaimer',
    'update_report_disclaimer',
    // AI Review page permissions
    'view_page_ai_review_dashboard',
    'view_page_ai_review_tasks',
    'view_page_ai_review_jobs',
    'view_page_ai_review_priorities',
    'view_page_ai_review_schedules',
    // Utilities page (staff-only)
    'view_page_utilities',
    'view_page_cik_matching',
    'view_page_security_issuer_matching',
    'view_page_fund_management',
    'view_page_adviser_mapping',
    'view_page_portfolio_issuer_matching',
    // Core content page permissions
    'view_page_dashboard',
    'view_page_issuers',
    'view_page_screens',
    'view_page_securities',
    'view_page_teams',
    'view_page_characteristics',
    'view_page_claims',
    'view_page_evidence',
    // Evidence CRUD permissions
    'create_evidence', 'read_evidence', 'update_evidence', 'delete_evidence',
    'view_page_portfolios',
    'view_page_profile',
    // Portfolio permissions
    'create_portfolio',
    'update_portfolio',
    // NPORT SEC holdings import (institutional and above)
    'update_nport_holdings',
    // Proxy voting permissions
    'view_page_proxy_voting',
    'update_proxy_tags',
    'delete_portfolio',
    // DEF 14A import permissions
    'create_def14a_import',
    // N-PX import permissions
    'view_page_npx_scheduler',
    'create_npx_import',
    // Proxy backfill permissions
    'view_page_proxy_backfill',
    // Screen permissions
    'create_screen',
    'update_screen',
    'delete_screen',
    // Security permissions
    'create_security',
    'update_security',
    // Issuer permissions
    'create_issuer',
    'update_issuer',
    'delete_issuer',
    // Claim management permissions (staff-only)
    'update_claim_characteristic',
    'update_claim_value',
    'migrate_claim_evidence',
    'delete_claim',
    // Issuer change tracking permissions (staff-only)
    'create_issuer_event',
    'update_issuer_review',
    'read_issuer_history',
    'view_page_issuer_change_review',
    // Team permissions
    'update_team',
    'delete_team',
    // Direct team membership management (staff-only)
    'create_team_membership_direct',
    'delete_team_membership_direct',
    // Export permissions (advisor and above)
    'read_evaluation_export',
    'read_evaluation_pdf',
    'read_screen_pdf',
    'read_characteristic_pdf',
    'read_issuer_pdf',
    // White-label PDF branding (institutional team admins)
    'update_team_branding',
    // Batch review permissions
    'create_batch_review',
    // Job scheduler permissions
    'update_scheduled_jobs',
    // AI cost permissions
    'read_ai_costs',
    // Proposal boundary repair permissions
    'update_proposal_boundaries',
    // Public note creation
    'create_public_note',
    // Institutional REST API key management
    'view_page_integrations',
    'create_api_key',
    'read_api_key',
    'update_api_key',
    'delete_api_key'
  ], []);

  // Fetch all granted permissions in a single RPC call
  const fetchAllPermissions = useCallback(async (): Promise<Set<string>> => {
    if (!user) return new Set();

    // Superadmin bypass on the client side (DB also handles this)
    if (isSuperadmin) return new Set(commonPermissions);

    // Staff go through RPC; regular users need team context
    if (!isStaff && !activeTeamId) return new Set();

    try {
      const { data, error } = await supabase.rpc('get_all_permissions', {
        check_user_id: user.id,
        check_team_id: activeTeamId
      });

      if (error) {
        console.error('Batch permission load error:', error);
        return new Set();
      }

      return new Set((data || []).map((row: { permission_id: string }) => row.permission_id));
    } catch (err) {
      console.error('Batch permission load failed:', err);
      return new Set();
    }
  }, [user, isSuperadmin, isStaff, activeTeamId, commonPermissions]);

  // Pre-load common permissions on mount and when active team changes
  useEffect(() => {
    async function loadPermissions() {
      setLoading(true);

      if (!user) {
        setPermissions({});
        setLoading(false);
        return;
      }

      const grantedSet = await fetchAllPermissions();

      // Build permissions map from the granted set
      const results: Record<string, boolean> = {};
      commonPermissions.forEach(p => {
        results[p] = grantedSet.has(p);
      });

      // Pre-populate cache so ad-hoc hasPermission() calls hit cache
      const now = Date.now();
      const teamKey = activeTeamId || 'staff';
      commonPermissions.forEach(p => {
        const cacheKey = `${user.id}:${p}:${teamKey}`;
        cacheRef.current.set(cacheKey, { result: results[p], timestamp: now });
      });

      setPermissions(results);
      setLoading(false);
    }

    // Clear cache when dependencies change
    invalidateCache();
    loadPermissions();
  }, [user, isSuperadmin, isStaff, activeTeamId, fetchAllPermissions, commonPermissions, invalidateCache]);

  // Load team plan when active team changes
  useEffect(() => {
    (async () => {
      if (activeTeamId) {
        await fetchTeamPlan(activeTeamId);
      } else {
        setTeamPlan(null);
      }
    })();
  }, [activeTeamId, fetchTeamPlan]);

  // Check public note permission via dedicated RPC (cross-team check).
  // Superadmin/staff/logged-out are pure derivations of props/state (no RPC needed);
  // only the regular-user case needs the effect below.
  const needsPublicNoteCheck = !!user && !isSuperadmin && !isStaff;

  useEffect(() => {
    if (!needsPublicNoteCheck) return;

    let cancelled = false;

    (async () => {
      setCheckedPublicNote(null);
      const { data } = await supabase.rpc('can_create_public_note');
      if (!cancelled) setCheckedPublicNote(data === true);
    })();

    return () => { cancelled = true; };
  }, [needsPublicNoteCheck]);

  const canCreatePublicNote = !user
    ? false
    : (isSuperadmin || isStaff)
      ? true
      : (checkedPublicNote ?? false);

  // Clear cache when user changes
  useEffect(() => {
    invalidateCache();
  }, [user?.id, invalidateCache]);

  // Convenience function to refresh permissions (called after team switch)
  const refresh = useCallback(async () => {
    invalidateCache();
    setLoading(true);

    if (activeTeamId) {
      await fetchTeamPlan(activeTeamId);
    }

    if (!user) {
      setPermissions({});
      setLoading(false);
      return;
    }

    const grantedSet = await fetchAllPermissions();

    const results: Record<string, boolean> = {};
    commonPermissions.forEach(p => {
      results[p] = grantedSet.has(p);
    });

    // Pre-populate cache
    const now = Date.now();
    const teamKey = activeTeamId || 'staff';
    commonPermissions.forEach(p => {
      const cacheKey = `${user.id}:${p}:${teamKey}`;
      cacheRef.current.set(cacheKey, { result: results[p], timestamp: now });
    });

    setPermissions(results);
    setLoading(false);
  }, [activeTeamId, fetchTeamPlan, fetchAllPermissions, user, commonPermissions, invalidateCache]);

  return {
    // Core functions
    hasPermission,
    checkPlanLimit,
    refresh,
    invalidateCache,

    // State
    permissions,
    loading,
    isSuperadmin,
    isStaff,
    teamPlan,
    activeTeamId,
    /** @deprecated Use activeTeamId instead */
    primaryTeamId: activeTeamId,

    // Convenience getters (from pre-loaded permissions)
    canUpdateContentVisibility: permissions.update_content_visibility ?? false,
    canCreateAiReview: permissions.create_ai_review ?? false,
    canUpdateAiPrompts: permissions.update_ai_prompts ?? false,
    canUpdateRolePermissions: permissions.update_role_permissions ?? false,
    canCreateCharacteristic: permissions.create_characteristic ?? false,
    canViewReviewSettings: permissions.view_component_ai_review_settings ?? false,
    canViewReviewHistory: permissions.view_component_ai_review_history ?? false,
    canViewSourceManagement: permissions.view_component_source_management ?? false,
    canViewResearchInsights: permissions.view_component_research_insights ?? false,
    canViewIssuerResearch: permissions.view_component_issuer_research ?? false,
    canViewActivityFeed: permissions.view_component_activity_feed ?? false,
    canViewHumanReviewStatus: permissions.view_component_human_review_status ?? false,
    canUpdateIssuerHumanReview: permissions.update_issuer_human_review ?? false,
    canReadAiCosts: permissions.read_ai_costs ?? false,
    canViewPromptConfig: permissions.view_page_prompt_config ?? false,
    canViewAdminUsers: permissions.view_page_admin_users ?? false,
    canViewAdminTeams: permissions.view_page_admin_teams ?? false,
    canViewAdminRoleEntitlements: permissions.view_page_admin_role_entitlements ?? false,
    canViewAdminPlanEntitlements: permissions.view_page_admin_plan_entitlements ?? false,
    canViewAdminAgentCoverage: permissions.view_page_admin_agent_coverage ?? false,

    // Reviews page permissions
    canViewReviewDashboard: permissions.view_page_ai_review_dashboard ?? false,
    canViewReviewTasks: permissions.view_page_ai_review_tasks ?? false,
    canViewReviewJobs: permissions.view_page_ai_review_jobs ?? false,
    canViewReviewPriorities: permissions.view_page_ai_review_priorities ?? false,

    // Utilities page permission (staff-only)
    canViewUtilities: permissions.view_page_utilities ?? false,

    // Admin page permissions
    canViewCIKMatching: permissions.view_page_cik_matching ?? false,
    canViewSecurityIssuerMatching: permissions.view_page_security_issuer_matching ?? false,
    canViewFundManagement: permissions.view_page_fund_management ?? false,
    canViewAdviserMapping: permissions.view_page_adviser_mapping ?? false,
    canViewPortfolioIssuerMatching: permissions.view_page_portfolio_issuer_matching ?? false,
    canViewAdminAudit: permissions.view_page_admin_audit ?? false,
    canViewUnmatchedHoldings: permissions.view_page_unmatched_holdings ?? false,
    canViewAssetCatMapping: permissions.view_page_asset_cat_mapping ?? false,
    canUpdateAssetCatMapping: permissions.update_asset_cat_mapping ?? false,

    // TOS Management
    canViewTosManagement: permissions.view_page_tos_management ?? false,
    canUpdateTos: permissions.update_tos ?? false,

    // Report Disclaimer Management
    canViewReportDisclaimer: permissions.view_page_report_disclaimer ?? false,
    canUpdateReportDisclaimer: permissions.update_report_disclaimer ?? false,

    // AI Review schedules page
    canViewReviewSchedules: permissions.view_page_ai_review_schedules ?? false,

    // Core content page permissions
    canViewDashboard: permissions.view_page_dashboard ?? false,
    canViewIssuers: permissions.view_page_issuers ?? false,
    canViewScreens: permissions.view_page_screens ?? false,
    canViewSecurities: permissions.view_page_securities ?? false,
    canViewTeams: permissions.view_page_teams ?? false,
    canViewCharacteristics: permissions.view_page_characteristics ?? false,
    canViewClaims: permissions.view_page_claims ?? false,
    canViewEvidence: permissions.view_page_evidence ?? false,
    canCreateEvidence: permissions.create_evidence ?? false,
    canReadEvidence: permissions.read_evidence ?? false,
    canUpdateEvidence: permissions.update_evidence ?? false,
    canDeleteEvidence: permissions.delete_evidence ?? false,
    canViewPortfolios: permissions.view_page_portfolios ?? false,
    canViewProfile: permissions.view_page_profile ?? false,

    // CRUD permissions for characteristics
    canReadCharacteristic: permissions.read_characteristic ?? false,
    canUpdateCharacteristic: permissions.update_characteristic ?? false,
    canDeleteCharacteristic: permissions.delete_characteristic ?? false,

    // CRUD permissions for issuer sources
    canReadIssuerSource: permissions.read_issuer_source ?? false,
    canUpdateIssuerSource: permissions.update_issuer_source ?? false,

    // CRUD permissions for issuer config
    canReadIssuerConfig: permissions.read_issuer_config ?? false,
    canUpdateIssuerConfig: permissions.update_issuer_config ?? false,

    // Portfolio permissions
    canCreatePortfolio: permissions.create_portfolio ?? false,
    canUpdatePortfolio: permissions.update_portfolio ?? false,
    canDeletePortfolio: permissions.delete_portfolio ?? false,

    // NPORT SEC holdings import (institutional and above)
    canUpdateNportHoldings: permissions.update_nport_holdings ?? false,

    // Screen permissions
    canCreateScreen: permissions.create_screen ?? false,
    canUpdateScreen: permissions.update_screen ?? false,
    canDeleteScreen: permissions.delete_screen ?? false,

    // Security permissions
    canCreateSecurity: permissions.create_security ?? false,
    canUpdateSecurity: permissions.update_security ?? false,

    // Issuer permissions
    canCreateIssuer: permissions.create_issuer ?? false,
    canUpdateIssuer: permissions.update_issuer ?? false,
    canDeleteIssuer: permissions.delete_issuer ?? false,

    // Claim management permissions (staff-only)
    canUpdateClaimCharacteristic: permissions.update_claim_characteristic ?? false,
    canUpdateClaimValue: permissions.update_claim_value ?? false,
    canMigrateClaimEvidence: permissions.migrate_claim_evidence ?? false,
    canDeleteClaim: permissions.delete_claim ?? false,

    // Issuer change tracking permissions (staff-only)
    canCreateIssuerEvent: permissions.create_issuer_event ?? false,
    canUpdateIssuerReview: permissions.update_issuer_review ?? false,
    canReadIssuerHistory: permissions.read_issuer_history ?? false,
    canViewPageIssuerChangeReview: permissions.view_page_issuer_change_review ?? false,

    // Proxy voting permissions
    canViewProxyVoting: permissions.view_page_proxy_voting ?? false,
    canUpdateProxyTags: permissions.update_proxy_tags ?? false,

    // DEF 14A import permissions
    canCreateDef14aImport: permissions.create_def14a_import ?? false,

    // N-PX import permissions
    canViewNpxScheduler: permissions.view_page_npx_scheduler ?? false,
    canCreateNpxImport: permissions.create_npx_import ?? false,

    // Proxy backfill permissions
    canViewProxyBackfill: permissions.view_page_proxy_backfill ?? false,

    // Team permissions
    canUpdateTeam: permissions.update_team ?? false,
    canDeleteTeam: permissions.delete_team ?? false,

    // Direct team membership management (staff-only)
    canCreateTeamMembershipDirect: permissions.create_team_membership_direct ?? false,
    canDeleteTeamMembershipDirect: permissions.delete_team_membership_direct ?? false,

    // Export permissions (advisor and above)
    canReadEvaluationExport: permissions.read_evaluation_export ?? false,
    canReadEvaluationPdf: permissions.read_evaluation_pdf ?? false,
    canReadScreenPdf: permissions.read_screen_pdf ?? false,
    canReadCharacteristicPdf: permissions.read_characteristic_pdf ?? false,
    canReadIssuerPdf: permissions.read_issuer_pdf ?? false,

    // White-label PDF branding (institutional team admins)
    canUpdateTeamBranding: permissions.update_team_branding ?? false,

    // Batch review permissions
    canCreateBatchReview: permissions.create_batch_review ?? false,

    // Job scheduler permissions
    canUpdateScheduledJobs: permissions.update_scheduled_jobs ?? false,

    // Proposal boundary repair permissions
    canUpdateProposalBoundaries: permissions.update_proposal_boundaries ?? false,

    // Public note creation (cross-team, via Contributors team)
    canCreatePublicNote,

    // Institutional REST API key management
    canViewIntegrations: permissions.view_page_integrations ?? false,
    canCreateApiKey:     permissions.create_api_key ?? false,
    canReadApiKey:       permissions.read_api_key ?? false,
    canUpdateApiKey:     permissions.update_api_key ?? false,
    canDeleteApiKey:     permissions.delete_api_key ?? false,
  };
}

/**
 * Hook to get team plan details and usage
 */
export function useTeamPlan(teamId?: string) {
  const { activeTeamId } = useEntitlements();
  const [plan, setPlan] = useState<TeamPlan | null>(null);
  const [usage, setUsage] = useState<{
    aiReviewsUsed: number;
    aiReviewsLimit: number | null;
    teamMembersUsed: number;
    teamMembersLimit: number | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const effectiveTeamId = teamId || activeTeamId;

  useEffect(() => {
    async function fetchPlanAndUsage() {
      if (!effectiveTeamId) {
        setPlan(null);
        setUsage(null);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Fetch plan
        const { data: teamData, error: teamError } = await supabase
          .from('team')
          .select(`
            plan_id,
            team_plan:plan_id (
              plan_id,
              plan_name,
              limits,
              features
            )
          `)
          .eq('team_id', effectiveTeamId)
          .single();

        if (teamError) throw teamError;

        const planData = teamData?.team_plan as any;
        if (planData) {
          setPlan({
            plan_id: planData.plan_id,
            plan_name: planData.plan_name,
            limits: planData.limits || {},
            features: planData.features || {}
          });
        }

        // Get AI review limit from plan_entitlement
        const { data: aiLimit } = await supabase
          .from('plan_entitlement')
          .select('usage_limit')
          .eq('plan_id', planData?.plan_id || 'free')
          .eq('permission_id', 'create_ai_review')
          .maybeSingle();

        // Fetch team member count
        const { count: memberCount } = await supabase
          .from('team_membership')
          .select('*', { count: 'exact', head: true })
          .eq('team_id', effectiveTeamId);

        // Get limit from limits JSONB (what admins edit in Plan Display Settings)
        // Note: limits JSONB uses llm_reviews_per_month key
        const reviewLimit = planData?.limits?.llm_reviews_per_month
          ?? planData?.limits?.ai_reviews_per_month
          ?? aiLimit?.usage_limit
          ?? null;

        setUsage({
          aiReviewsUsed: 0, // Usage tracking removed
          aiReviewsLimit: reviewLimit,
          teamMembersUsed: memberCount || 0,
          teamMembersLimit: planData?.limits?.team_members ?? null
        });
      } catch (err) {
        console.error('Failed to fetch plan and usage:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchPlanAndUsage();
  }, [effectiveTeamId]);

  // Upgrade plan function (stubbed for Stripe integration)
  const upgradePlan = useCallback(async (newPlanId: string) => {
    if (!effectiveTeamId) {
      throw new Error('No team selected');
    }

    // For now, allow direct plan changes (for testing)
    // In production, this would redirect to Stripe Checkout
    const { error } = await supabase
      .from('team')
      .update({ plan_id: newPlanId })
      .eq('team_id', effectiveTeamId);

    if (error) {
      throw error;
    }

    // Refresh plan data
    window.location.reload();
  }, [effectiveTeamId]);

  return { plan, usage, loading, upgradePlan };
}

/**
 * Hook to fetch available plans for comparison.
 * Excludes internal plans like 'staff' that shouldn't be shown to users.
 */
export function usePlans() {
  const [plans, setPlans] = useState<TeamPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPlans() {
      try {
        const { data, error } = await supabase
          .from('team_plan')
          .select('*')
          .eq('is_active', true)
          .eq('is_hidden', false) // Exclude internal plans (e.g. staff)
          .order('display_order');

        if (error) throw error;

        setPlans(data?.map(p => ({
          plan_id: p.plan_id,
          plan_name: p.plan_name,
          limits: p.limits || {},
          features: p.features || {}
        })) || []);
      } catch (err) {
        console.error('Failed to fetch plans:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchPlans();
  }, []);

  return { plans, loading };
}
